import { useMemo } from 'react';
import ReactECharts from 'echarts-for-react';
import type { EChartsOption } from 'echarts';
import type { BacktestComparisonResult, ComparisonStrategy } from '@/api/mediumTermBacktest';
import {
  deriveComparisonExplanation,
  formatNumber,
  formatQuantity,
  formatSigned,
  shortDate,
  totalPnlFromDaily,
} from './viewModel';

function StrategyRow({
  strategy,
  view,
}: {
  strategy: ComparisonStrategy;
  view: ReturnType<typeof deriveComparisonExplanation>['summaries'][ComparisonStrategy];
}) {
  const values = [
    `${view.openingLabel} ${formatQuantity(view.openedMwh)} MWh`,
    `成交均价 ${view.weightedExecutionPrice == null ? '-' : formatNumber(view.weightedExecutionPrice)}`,
    `现货均价 ${view.weightedSpotPrice == null ? '-' : formatNumber(view.weightedSpotPrice)}`,
    `价差 ${view.priceEdge == null ? '-' : `${formatSigned(view.priceEdge)} 元/MWh`}`,
    `已实现盈亏 ${formatSigned(view.realizedPnl)} 元`,
    `终值盈亏 ${formatSigned(view.terminalPnl)} 元`,
    `总盈亏 ${formatSigned(view.totalPnl)} 元`,
  ];
  return (
    <div
      className={`mtb-strategy-row mtb-strategy-row--${strategy.toLowerCase()}`}
      data-testid={`strategy-summary-${strategy}`}
    >
      <strong>{view.label}</strong>
      {values.map((value) => <span key={value}>{value}</span>)}
    </div>
  );
}

export default function StrategyExplanationPanel({ result }: { result: BacktestComparisonResult }) {
  const view = useMemo(() => deriveComparisonExplanation(result), [result]);
  const option: EChartsOption = useMemo(() => ({
    animation: false,
    tooltip: { trigger: 'axis', valueFormatter: (value) => `${formatNumber(Number(value))} 元` },
    legend: { top: 0, right: 8, itemWidth: 14, textStyle: { color: '#475467', fontSize: 11 } },
    grid: { left: 64, right: 58, top: 38, bottom: 40 },
    xAxis: {
      type: 'category',
      data: view.contributions.map((item) => shortDate(item.targetDate)),
      axisLabel: { color: '#667085', fontSize: 10, interval: 'auto' },
    },
    yAxis: [
      { type: 'value', name: '单日优势 元', nameTextStyle: { color: '#667085' }, splitLine: { lineStyle: { color: '#edf0f3' } } },
      { type: 'value', name: '累计 元', nameTextStyle: { color: '#667085' }, splitLine: { show: false } },
    ],
    series: [
      {
        name: '反转PnL - 动量PnL',
        type: 'bar',
        barMaxWidth: 18,
        data: view.contributions.map((item) => ({
          value: item.value,
          itemStyle: { color: item.value >= 0 ? '#148274' : '#c34a36' },
        })),
      },
      {
        name: '累计优势',
        type: 'line',
        yAxisIndex: 1,
        symbol: 'none',
        data: view.contributions.map((item) => item.cumulative),
        lineStyle: { color: '#a66c00', width: 2 },
      },
    ],
  }), [view]);

  const flatKey = result.role === 'buyer' ? 'BUYER_FLAT' : 'SELLER_FLAT';
  const zero = result.benchmarks.SELLER_ZERO;
  const flat = result.benchmarks[flatKey];

  return (
    <section className="mtb-explanation" aria-labelledby="mtb-explanation-title">
      <div className="mtb-section-head">
        <div>
          <span className="mtb-kicker">整月结果解释</span>
          <h3 id="mtb-explanation-title">{view.headline}</h3>
        </div>
        <span className="mtb-exploratory-note">探索性历史解释，不代表未来月份结论</span>
      </div>

      <div className="mtb-strategy-rows">
        <StrategyRow strategy="MOMENTUM" view={view.summaries.MOMENTUM} />
        <StrategyRow strategy="REVERSAL" view={view.summaries.REVERSAL} />
      </div>

      <div className="mtb-benchmark-inline">
        <span>基准参考</span>
        {zero ? <span>空仓 {formatSigned(totalPnlFromDaily(zero))} 元</span> : null}
        {flat ? <span>被动平铺 {formatSigned(totalPnlFromDaily(flat))} 元</span> : null}
        <span>Oracle {formatSigned(totalPnlFromDaily(result.oracle))} 元，可在行动线下钻</span>
      </div>

      <div className="mtb-chart-shell">
        <div className="mtb-chart-title">
          <strong>优势从哪些标的日产生</strong>
          <span>柱为反转PnL减动量PnL；正值支持反转，负值抵消反转优势</span>
        </div>
        <ReactECharts option={option} style={{ height: 300 }} notMerge />
      </div>

      <div className="mtb-contributor-grid">
        <div>
          <strong>主要正贡献</strong>
          {view.positiveContributors.length ? view.positiveContributors.map((item) => (
            <span key={item.targetDate}>{shortDate(item.targetDate)} {formatSigned(item.value)} 元</span>
          )) : <span>无正贡献标的日</span>}
        </div>
        <div>
          <strong>主要负贡献</strong>
          {view.negativeContributors.length ? view.negativeContributors.map((item) => (
            <span key={item.targetDate}>{shortDate(item.targetDate)} {formatSigned(item.value)} 元</span>
          )) : <span>无负贡献标的日</span>}
        </div>
      </div>
    </section>
  );
}
